/**
 * How a composed revision reads in the creator's terms: its stages
 * (plan → the plan's steps → synthesis → build) as `ComposedCreateProgress`
 * shows them, and what the callable's refusals ask the creator to do.
 */
import type { ComposedRevisionViewV1, Creation, CreationVersion } from '@djgg/creations';
import { isComposedPipeline } from '@djgg/creations';
import type { ComposedStageView } from './wizard/ComposedCreateProgress.js';
import { COMPOSED_STAGE_LABELS } from './wizard/composedCreate.js';

/** Why a slot kept its art, in the creator's terms. */
export const COMPOSED_REVISION_KEPT_REASON = {
  'same-basis': 'same art basis',
  'creator-attached': 'your uploaded art',
} as const;

/**
 * The run's stages. The steps shown are the saved ones plus the one the run
 * is on; `rebuild` is a generic game rebuilt with the composed builder;
 * `advancing` is true while this page is waiting on the server.
 */
export function composedRevisionStages(run: ComposedRevisionViewV1 | null, rebuild: boolean, advancing: boolean): ComposedStageView[] {
  const onStep = run ? /^step-(\d+)$/.exec(run.stage) : null;
  const steps = run ? Math.max(run.stepsDone, onStep ? Number(onStep[1]) + 1 : 0) : 0;
  const stages = [
    { id: 'plan', label: COMPOSED_STAGE_LABELS.plan },
    ...Array.from({ length: steps }, (_, index) => ({ id: `step-${index}`,
      label: rebuild ? `Building part ${index + 1} of your game with the new builder` : `Building part ${index + 1} of your change` })),
    { id: 'synthesis', label: COMPOSED_STAGE_LABELS.synthesis },
    { id: 'build', label: COMPOSED_STAGE_LABELS.build },
  ];
  const current = run ? stages.findIndex(stage => stage.id === run.stage) : 0;
  return stages.map((stage, index): ComposedStageView => {
    if (run?.status === 'accepted' || index < current) return { ...stage, status: 'done' };
    if (index > current) return { ...stage, status: 'pending' };
    if (run?.status === 'failed') return { ...stage, status: 'error' };
    return { ...stage, status: run?.status === 'claimed' || advancing ? 'running' : 'pending' };
  });
}

/**
 * The version carrying an accepted run's build: the build itself while it is
 * the latest or the live version, or the latest version when it is a retry of
 * that build (requestBuild's composed retry re-admits the failed revision as a
 * new composed version of the same live version, with no build mode, which no
 * other build of a live composed game is). Null before the run is accepted and
 * once another build has replaced its build.
 */
export function composedRevisionBuildVersionV1(
  run: ComposedRevisionViewV1 | null,
  creation: Pick<Creation, 'latestVersion' | 'activeVersion'>,
  latest: Pick<CreationVersion, 'n' | 'buildMode' | 'pipelineSnapshot'> | null,
): number | null {
  const build = run?.status === 'accepted' ? run.build ?? null : null;
  if (!build) return null;
  if (creation.latestVersion <= build.version || creation.activeVersion === build.version) return build.version;
  const retry = latest !== null && latest.n === creation.latestVersion && latest.n > build.version
    && latest.buildMode === undefined && isComposedPipeline(latest.pipelineSnapshot)
    && (creation.activeVersion === run!.base.version || creation.activeVersion === latest.n);
  return retry ? latest.n : null;
}

/**
 * Whether a failed change has ended: the creator can only start a new change.
 * The panel offers a change no retry: any failed stage ends it, the refused
 * last, unpaid build request included (owner decisions 199, 201). A conversion
 * has no request to ask differently, so it keeps its retry.
 */
export function composedChangeEndedV1(run: ComposedRevisionViewV1 | null, refusal: ComposedRevisionCallErrorV1 | null): boolean {
  if (!run?.request) return false;
  return run.status === 'failed' || refusal?.kind === 'paid-retry' || refusal?.kind === 'retry-limit';
}

/** A refused revision call, by what the creator can do next. */
export type ComposedRevisionCallErrorV1 =
  /** A maintained profile without revisions: nothing to run. */
  | { kind: 'profile-unsupported'; message: string }
  /** A paid stage may have contacted its provider: retry only as an approved paid retry. */
  | { kind: 'paid-retry'; message: string }
  /** This run has used its retries: only a new change can follow. */
  | { kind: 'retry-limit'; message: string }
  /** An old unstarted conversion plan needs fresh preservation coverage. */
  | { kind: 'conversion-replan'; message: string }
  /** Another change is in progress: follow that run. */
  | { kind: 'in-progress'; message: string }
  /** The live game changed since the run started: start a new change. */
  | { kind: 'base-changed'; message: string }
  /** The change would leave the game as it is: nothing was built, start a new change. */
  | { kind: 'no-change'; message: string }
  /** This page's run is no longer the game's current one: read it again. */
  | { kind: 'stale'; message: string }
  /** Permission, admission, budget or transport: nothing the page can act on. */
  | { kind: 'other'; message: string };

/** Classify a callable failure (`functions/<code>` with the handler's `details`). */
export function composedRevisionCallErrorV1(error: unknown): ComposedRevisionCallErrorV1 {
  const failure = (error && typeof error === 'object' ? error : {}) as { code?: unknown; message?: unknown; details?: unknown };
  const message = typeof failure.message === 'string' && failure.message ? failure.message : 'This change could not continue.';
  const details = (failure.details && typeof failure.details === 'object' ? failure.details : {}) as { code?: unknown };
  if (failure.code === 'functions/invalid-argument' && details.code === 'profile-unsupported') return { kind: 'profile-unsupported', message };
  if (failure.code === 'functions/invalid-argument' && details.code === 'conversion-replan-required') return { kind: 'conversion-replan', message };
  if (failure.code === 'functions/not-found') return { kind: 'stale', message };
  if (failure.code === 'functions/already-exists') {
    if (details.code === 'base-changed') return { kind: 'base-changed', message };
    if (details.code === 'no-change') return { kind: 'no-change', message };
    if ('runId' in details) return { kind: 'in-progress', message };
    if (details.code === 'paid-retry-required') return { kind: 'paid-retry', message };
    if (details.code === 'retry-limit-reached') return { kind: 'retry-limit', message };
  }
  return { kind: 'other', message };
}
